import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDateTime, formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Calendar as ShadCalendar } from "../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CalendarBlank, Plus, Clock, Trash, ChatCircleDots, CheckCircle, Camera, UploadSimple, X, UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import CustomerAutocomplete from "./components/CustomerAutocomplete";
import AppointmentPhotosDialog from "./components/AppointmentPhotosDialog";

const WASH = ["Detailing Express", "Detailing Exterior", "Servicio Completo", "Detailing Premium", "Cambio de aceite", "Pre-ITV", "Diagnosis OBD-II"];

export default function CalendarView() {
  const { user, isSolo } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [photoAppt, setPhotoAppt] = useState(null);  // Sprint D · Galería matrículas
  const [customer, setCustomer] = useState(null);
  const [form, setForm] = useState({
    license_plate: "", customer_name: "", customer_phone: "", customer_email: "",
    wash_type: "Lavado Completo", time: "10:00", operario_id: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [a, u] = await Promise.all([
      api.get("/appointments"),
      user.role === "admin" ? api.get("/users") : Promise.resolve({ data: [user] }),
    ]);
    setItems(a.data); setUsers(u.data);
    if (!form.operario_id && u.data.length) setForm((f)=>({ ...f, operario_id: u.data[0].id }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const dayKey = (d) => new Date(d).toISOString().slice(0, 10);
  const sel = dayKey(selectedDate);
  const dayItems = items.filter((a) => dayKey(a.date) === sel);
  const upcoming = items
    .filter((a) => new Date(a.date) >= new Date(new Date().setHours(0,0,0,0)))
    .slice(0, 8);

  const appointmentDates = items.map((a) => new Date(a.date));

  const onPickCustomer = (c) => {
    setCustomer(c);
    setForm((f) => ({
      ...f,
      customer_name: c.name || f.customer_name,
      customer_email: c.email || f.customer_email,
      customer_phone: c.phone || f.customer_phone,
      license_plate: c.main_license_plate || f.license_plate,
    }));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const [h, m] = form.time.split(":").map(Number);
      const d = new Date(selectedDate);
      d.setHours(h, m || 0, 0, 0);
      await api.post("/appointments", {
        date: d.toISOString(),
        license_plate: form.license_plate,
        customer_id: customer?.id || null,
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_email: form.customer_email,
        wash_type: form.wash_type,
        operario_id: form.operario_id || user.id,
        notes: form.notes,
      });
      toast.success("Cita reservada — emails de confirmación enviados");
      setOpen(false);
      setCustomer(null);
      setForm({ ...form, license_plate: "", customer_name: "", customer_phone: "", customer_email: "", notes: "" });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cancelAppt = async (a) => {
    const ok = window.confirm(`¿Anular la cita de ${a.license_plate} (${new Date(a.date).toLocaleString("es-ES")})?\n\nEl cliente NO será notificado automáticamente — avísale si procede.`);
    if (!ok) return;
    try {
      await api.delete(`/appointments/${a.id}`);
      toast.success("Cita anulada — slot liberado");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const notifyReady = async (a) => {
    if (!a.customer_phone && !a.customer_email) {
      toast.error("Esta cita no tiene teléfono ni email");
      return;
    }
    if (!window.confirm(`Avisar al cliente que su coche ${a.license_plate} está listo?`)) return;
    try {
      const { data } = await api.post(`/appointments/${a.id}/notify-ready`);
      const em = data?.result?.email;
      const push = data?.result?.push;
      const parts = [];
      if (push?.sent) parts.push("Push enviado");
      if (em?.sent) parts.push("email enviado");
      else if (em && em.reason) parts.push(`email: ${em.reason}`);
      toast.success(parts.length ? parts.join(" · ") : "Aviso registrado");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Agenda online</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1">Calendario de citas</h1>
        </div>
        <Button onClick={()=>setOpen(true)} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="new-appt-btn">
          <Plus className="mr-1.5" /> Nueva cita
        </Button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-gray-200 rounded-sm p-4">
          <ShadCalendar
            mode="single"
            selected={selectedDate}
            onSelect={(d)=> d && setSelectedDate(d)}
            modifiers={{ booked: appointmentDates }}
            modifiersClassNames={{ booked: "after:content-[''] after:block after:w-1 after:h-1 after:bg-[var(--ag-gold)] after:rounded-full after:mx-auto" }}
            className="rounded-sm"
            data-testid="calendar"
          />
        </div>
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-gray-200 rounded-sm p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2">
              <CalendarBlank size={14} /> {selectedDate.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
            </div>
            <div className="mt-4 space-y-2">
              {dayItems.length === 0 && <div className="text-sm text-gray-500 py-4 text-center">Sin citas este día</div>}
              {dayItems.map((a) => {
                const startD = new Date(a.date);
                const dur = a.duration_min || 60;
                const endD = new Date(startD.getTime() + dur * 60000);
                const fmt = (d) => d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
                return (
                <div key={a.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 border border-gray-200 rounded-sm hover:border-gray-400 transition-colors" data-testid={`appt-${a.id}`}>
                  <div className="flex items-start sm:items-center gap-3 min-w-0">
                    <div className="font-mono-tab text-sm tabular-nums whitespace-nowrap">
                      <Clock size={12} className="inline mr-1" />
                      <span data-testid={`appt-time-range-${a.id}`}>{fmt(startD)} – {fmt(endD)}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="truncate">{a.license_plate} · {a.wash_type}</span>
                        {a.ready_notified_at && (
                          <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1">
                            <CheckCircle size={10} weight="fill" /> Listo
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{a.customer_name || "—"}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-1 sm:shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm h-8 text-xs gap-1.5 flex-1 sm:flex-none min-w-0"
                      onClick={()=>navigate(`/chats?customer_id=${a.customer_id || ''}&customer_account_id=${a.customer_account_id || ''}`)}
                      disabled={!a.customer_id && !a.customer_account_id}
                      data-testid={`appt-chat-${a.id}`}
                      title="Abrir chat interno con el cliente"
                    >
                      <ChatCircleDots size={12} weight="fill" className="text-[var(--ag-gold)] shrink-0" />
                      <span className="truncate">Chat</span>
                    </Button>
                    {a.customer_account_id && !a.customer_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-sm h-8 text-xs gap-1.5 flex-1 sm:flex-none min-w-0 border-amber-400 text-amber-700 hover:bg-amber-50"
                        onClick={async ()=>{
                          try {
                            const { data } = await api.post(`/customers/from-appointment/${a.id}`);
                            toast.success(data.created ? `Cliente añadido a tu CRM: ${data.customer.name}` : `Ya estaba en tu CRM: ${data.customer.name}`);
                            load();
                          } catch (err) {
                            toast.error(formatErr(err) || "No se pudo añadir");
                          }
                        }}
                        data-testid={`add-to-crm-${a.id}`}
                        title="Añadir este cliente del marketplace a tu base de datos local"
                      >
                        <UserPlus size={12} weight="fill" className="text-amber-600 shrink-0" />
                        <span className="truncate">Añadir a CRM</span>
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm h-8 text-xs gap-1.5 flex-1 sm:flex-none min-w-0"
                      onClick={()=>notifyReady(a)}
                      data-testid={`notify-ready-${a.id}`}
                      title="Avisar al cliente vía push/chat que su coche está listo"
                    >
                      <CheckCircle size={12} weight="fill" className="text-emerald-600 shrink-0" />
                      <span className="truncate">{a.ready_notified_at ? "Reenviar aviso" : "Coche listo"}</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-sm h-8 text-xs gap-1.5 flex-1 sm:flex-none min-w-0"
                      onClick={()=>setPhotoAppt(a)}
                      data-testid={`appt-photos-${a.id}`}
                    >
                      <Camera size={12} weight="fill" className="text-[var(--ag-gold)] shrink-0" />
                      <span className="truncate">Fotos {(a.photos?.length || 0) > 0 && `(${a.photos.length})`}</span>
                    </Button>
                    <Button size="sm" variant="outline" onClick={()=>cancelAppt(a)} className="rounded-sm h-8 text-xs gap-1.5 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-500 flex-1 sm:flex-none min-w-0" data-testid={`cancel-appt-${a.id}`}>
                      <Trash size={12} className="shrink-0" /> <span className="truncate">Anular</span>
                    </Button>
                  </div>
                </div>
              );})}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-sm p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Próximas citas</div>
            <div className="mt-3 space-y-1">
              {upcoming.length === 0 && <div className="text-sm text-gray-500">No hay citas próximas.</div>}
              {upcoming.map((a)=>(
                <div key={a.id} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-100 last:border-0">
                  <span className="font-mono-tab text-xs text-gray-500">{fmtDateTime(a.date)}</span>
                  <span className="flex-1 px-3 truncate">{a.license_plate} · {a.wash_type}</span>
                  <span className="text-xs text-gray-500">{users.find(u=>u.id===a.operario_id)?.name || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o)=>{ setOpen(o); if (!o) setCustomer(null); }}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl tracking-tighter">Nueva cita · {selectedDate.toLocaleDateString("es-ES")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* NEW: Autocompletado CRM */}
            <CustomerAutocomplete
              value={customer}
              onSelect={onPickCustomer}
              onClear={() => { setCustomer(null); }}
              testId="ap-cust"
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Matrícula</Label>
                <Input value={form.license_plate} onChange={(e)=>setForm({...form, license_plate: e.target.value.toUpperCase()})} className="mt-1 rounded-sm font-mono-tab" data-testid="ap-plate" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Hora <span className="normal-case text-[var(--ag-text-muted)]">(en punto)</span></Label>
                <Input type="time" step="3600" value={form.time} onChange={(e)=>setForm({...form, time: e.target.value.slice(0,2) + ":00"})} className="mt-1 rounded-sm" data-testid="ap-time" />
              </div>
            </div>
            {!customer && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Cliente <span className="text-[var(--ag-text-dim)] normal-case tracking-normal">(o usa CRM ↑)</span></Label>
                    <Input value={form.customer_name} onChange={(e)=>setForm({...form, customer_name: e.target.value})} className="mt-1 rounded-sm" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Teléfono <span className="text-[var(--ag-text-dim)] normal-case tracking-normal">(opcional · solo CRM interno)</span></Label>
                    <Input value={form.customer_phone} onChange={(e)=>setForm({...form, customer_phone: e.target.value})} className="mt-1 rounded-sm" placeholder="+34..." />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Email del cliente (opcional — recibe confirmación)</Label>
                  <Input type="email" value={form.customer_email} onChange={(e)=>setForm({...form, customer_email: e.target.value})} className="mt-1 rounded-sm" data-testid="ap-customer-email" />
                </div>
              </>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider">Tipo de servicio</Label>
              <Select value={form.wash_type} onValueChange={(v)=>setForm({...form, wash_type: v})}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{WASH.map((w)=> <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {user.role === "admin" && !isSolo && (
              <div>
                <Label className="text-xs uppercase tracking-wider">Operario</Label>
                <Select value={form.operario_id} onValueChange={(v)=>setForm({...form, operario_id: v})}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>{users.map((u)=> <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider">Notas</Label>
              <Input value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="mt-1 rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={submit} disabled={busy || !form.license_plate} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="ap-submit">{busy ? "Guardando…" : "Reservar cita"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sprint D · Galería de Matrículas (anti-disputas) */}
      <AppointmentPhotosDialog
        appt={photoAppt}
        open={!!photoAppt}
        onOpenChange={(v)=>{ if (!v) setPhotoAppt(null); }}
        onUpdated={()=>load()}
      />
    </div>
  );
}
